/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useState } from 'react';
import { UiController, consoleCommands, events } from 'modules/common/ui-controller';
import useCommandRouter from 'modules/common/ui-controller/hooks/use-command-router';
import { useDispatch, useSelector } from 'react-redux';
import { dashboardActions } from 'modules/dashboard/slice';
import { selectShareUrlTabList, selectTabList, selectUrlTab } from '../selectors';
import { isEmpty } from '../../common/helpers/object';
import COMMAND_ROUTES from './command-routes';

/**
 * Custom hook to execute main command flow
 * Sets the tab of a command url, and forwards the other commands to that tab once it is active,
 * see COMMAND_ROUTES. The tab applies them in order, see the *-command-steps.js file of each tab.
 * @param {Number} tabValue
 * @param {Function} setTabValue
 */
const useMainCommandFlow = (tabValue, setTabValue) => {
  const dispatch = useDispatch();
  const urlTab = useSelector(selectUrlTab);
  const TAB_VALUE = useSelector(selectTabList);
  const tabs = useSelector(selectShareUrlTabList);
  //
  const [tabState, setTabState] = useState(null);
  // forward the commands of the command url to the active tab
  useCommandRouter(COMMAND_ROUTES, tabValue, urlTab, TAB_VALUE);
  // Triggered when tab list value is set
  useEffect(() => {
    if (!isEmpty(tabs)) {
      setTabValue(tabs[tabState?.value] ?? 0);
      dispatch(dashboardActions.setTabChange(true));
    }
  }, [tabState, tabs, dispatch, setTabValue]);
  /**
   * Triggers in first component rendering
   * Add listener functions to the commands that are not forwarded to a tab
   */
  useEffect(() => {
    // Listener function to dispatch function to set tab value
    const setTabHandler = (data) => {
      setTabState({ value: data });
    };
    // Listener function to keep pms sync command value for the tab of the command url - pms
    const setPmsSyncValue = (data) => {
      dispatch(dashboardActions.setCommandRunValue({ key: consoleCommands.PMS, value: data }));
    };
    // Listener function to keep vi response command value for the tab of the command url
    const setViResponseHandler = (data) => {
      dispatch(dashboardActions.setCommandRunValue({ key: consoleCommands.VI, value: data }));
    };
    UiController.subscribe(events.SET_TAB, setTabHandler);
    UiController.subscribe(events.SET_PMS_SYNC, setPmsSyncValue);
    UiController.subscribe(events.SET_VI_RESPONSE, setViResponseHandler);
    return () => {
      UiController.unsubscribe(events.SET_TAB, setTabHandler);
      UiController.unsubscribe(events.SET_PMS_SYNC, setPmsSyncValue);
      UiController.unsubscribe(events.SET_VI_RESPONSE, setViResponseHandler);
    };
  }, [dispatch]);
};
//
export default useMainCommandFlow;
